import { useEffect } from 'react';

import { Loader2, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';

import OrderSummary from 'components/OrderSummary';
import CustomerForm from 'components/CustomerForm';
import PaymentForm from 'components/PaymentForm';
import Registration from 'registration/Registration';
import ReviewCartPopup from 'components/ReviewCartPopup';

import { Trans, t } from '@lingui/macro';
import { useCheckoutContext } from 'context/CheckoutContext';
import SeatsPicker from './SeatsPicker';
import Tickets from './Tickets';

export default function Checkout({
    reviewCartOpen,
    onReviewCartClose,
    onReviewCartContinue,
    onStartOver,
}) {
    const {
        event,
        paidTransaction,
        status,
        contentRef,
        registrationForm,
        seatsPicked,
        chartSelectionErrors,
        sessionAlert,
        closeSessionAlert,
        chartKey,
        deselectSeat,
    } = useCheckoutContext();

    useEffect(() => {
        if (!sessionAlert.open || sessionAlert.severity === 'error') {
            return undefined;
        }
        const timer = setTimeout(() => {
            closeSessionAlert();
        }, 6000);
        return () => clearTimeout(timer);
    }, [sessionAlert.open, sessionAlert.severity, closeSessionAlert]);

    return (
        <div className="min-h-full flex-1 bg-background text-foreground">
            <div ref={contentRef} />

            {sessionAlert.open ? (
                <div className="pointer-events-none fixed left-0 right-0 top-4 z-50 flex justify-center px-4">
                    <div className="pointer-events-auto w-full max-w-lg shadow-lg">
                        <Alert
                            variant={sessionAlert.severity === 'error' ? 'destructive' : 'warning'}
                            className="flex items-start gap-2 pr-2 [&>svg~*]:pl-0"
                        >
                            <AlertDescription className="flex-1 pt-0.5">
                                {sessionAlert.message}
                            </AlertDescription>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0 text-current hover:bg-black/10 dark:hover:bg-white/10"
                                onClick={closeSessionAlert}
                                aria-label={t`Dismiss`}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </Alert>
                    </div>
                </div>
            ) : null}

            {status === 'Loading' && (
                <div className="flex h-[360px] w-full items-center justify-center text-foreground">
                    <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
                </div>
            )}

            {status === 'Error' && (
                <div className="flex h-[360px] w-full items-center justify-center text-foreground">
                    <p>
                        <Trans>
                            Unable to proceed with checkout. Please close this window and try again.
                        </Trans>
                    </p>
                </div>
            )}

            <ReviewCartPopup
                open={reviewCartOpen}
                onClose={onReviewCartClose}
                onContinue={onReviewCartContinue}
                onStartOver={onStartOver}
                onRemoveSeat={deselectSeat}
                seatsPicked={seatsPicked}
                chartSelectionErrors={chartSelectionErrors}
                currency={event.currency || 'CAD'}
            />

            <motion.div className="relative">
                <AnimatePresence mode="wait">
                    {status === 'SeatPicker' && (
                        <motion.div
                            key={`seatPicker-${chartKey}`}
                            initial={false}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                type: 'tween',
                                ease: [0.4, 0, 0.2, 1],
                                duration: 0.4,
                            }}
                        >
                            <SeatsPicker />
                        </motion.div>
                    )}
                    {status === 'Tickets' && (
                        <motion.div
                            key="tickets"
                            initial={false}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                type: 'tween',
                                ease: [0.4, 0, 0.2, 1],
                                duration: 0.4,
                            }}
                        >
                            <Tickets />
                        </motion.div>
                    )}

                    {status === 'Registration' && registrationForm !== null && (
                        <motion.div
                            key="registration"
                            initial={false}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                type: 'tween',
                                ease: [0.4, 0, 0.2, 1],
                                duration: 0.4,
                            }}
                        >
                            <Registration />
                        </motion.div>
                    )}

                    {(status === 'Checkout' || status === 'Submit') && (
                        <motion.div
                            key="checkout"
                            initial={false}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                type: 'tween',
                                ease: [0.4, 0, 0.2, 1],
                                duration: 0.4,
                            }}
                        >
                            <div className="mx-auto flex w-full max-w-screen-xl flex-row flex-wrap pb-0 pt-8">
                                <div className="w-full p-4 md:w-[60%]">
                                    <CustomerForm />
                                    {paidTransaction && <PaymentForm />}
                                    <div className="block md:hidden">
                                        <OrderSummary />
                                    </div>
                                </div>
                                <div className="hidden w-[40%] p-4 md:block">
                                    <OrderSummary />
                                </div>
                            </div>
                            <div className="mx-auto w-full max-w-screen-xl pb-0 pt-12">
                                <div className="my-4 mx-2">
                                    <p className="whitespace-pre-wrap text-xs text-muted-foreground">
                                        {event.finePrint || ''}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
}
